import { ElementCategoryID } from '../models/element-categories.model';
import type {
  AreaUnit,
  ThicknessUnit,
} from '../models/element-quantities.model';
import type { IElement } from '../models/project.model';
import { Orientation } from '../models/orientation.model';
import { getElementCategory } from './element-category.helpers';
import { getElementQuantityResolvedCountByName } from './element-quantity-core.helpers';

/**
 * Get in which unit a "thickness" should be measured. For walls thickness in width, for floors/slabs it's height.
 */
export const getThicknessUnit = (element: IElement): ThicknessUnit => {
  const thicknessUnit = getThicknessUnitByCategoryId(element.category_id);
  if (thicknessUnit) {
    return thicknessUnit;
  }

  return getElementQuantityResolvedCountByName(element, 'height', 0) >
    getElementQuantityResolvedCountByName(element, 'width', 0)
    ? 'height'
    : 'width';
};

/**
 * Get in which unit a "thickness" should be measured based on the category.
 */
export const getThicknessUnitByCategoryId = (
  categoryId?: ElementCategoryID,
): ThicknessUnit | undefined => {
  const category = getElementCategory(categoryId);
  if (category?.orientation === Orientation.Horizonal) {
    return 'height';
  }
  if (category?.orientation === Orientation.Vertical) {
    return 'width';
  }
};

/**
 * Get in which unit an "area" should be measured. For walls area_side, for floors/slabs it's area_top.
 */
export const getAreaUnit = (element: IElement): AreaUnit => {
  const category = getElementCategory(element);
  if (category?.orientation === Orientation.Horizonal) {
    return 'area_top';
  }
  if (category?.orientation === Orientation.Vertical) {
    return 'area_side';
  }

  return getElementQuantityResolvedCountByName(element, 'area_side', 0) >
    getElementQuantityResolvedCountByName(element, 'area_top', 0)
    ? 'area_side'
    : 'area_top';
};

/**
 * Get the area of an element (walls: area_side, floors/slabs: area_top).
 */
export const getElementArea = (element: IElement): number => {
  const areaUnit = getAreaUnit(element);
  return getElementQuantityResolvedCountByName(element, areaUnit, 0);
};
